import { useCallback, useEffect, useRef } from 'react';

import type { Player } from '@schibsted-svp/web-player';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';

import { usePlayerEvent } from '../utils/usePlayerEvent';
import { recreatePlayer } from '../../player/playerService';
import { resolveIn } from '../../utils/async';
import { usePlayer } from '../../player/hooks/usePlayer';
import { usePlayerState } from '../../player/hooks/usePlayerState';

import type { RecoveryMetadata } from './helpers';
import { playerErrorMetadata } from './helpers';

const RECOVER_FROM_ERROR_THRESHOLD_MS = 3000;

const isPlayingState = (
  state: ReturnType<Player['getState']> = 'idle'
): boolean => {
  return ['playing', 'buffering', 'loading'].includes(state);
};

export const useErrorRecovery = () => {
  const player = usePlayer();
  const state = usePlayerState();
  const lastRecoveryTimeRef = useRef(0);
  const metadataRef = useRef<RecoveryMetadata>({
    assetId: player?.getRawAsset()?.id,
    lastKnownPosition: 0,
    isPlaying: false,
  });

  useEffect(() => {
    if (isPlayingState(state)) {
      metadataRef.current.isPlaying = true;
    } else if (state === 'paused') {
      metadataRef.current.isPlaying = false;
    } // ignore other states
  }, [state]);

  const handleNewAsset = useCallback(() => {
    const asset = player?.getRawAsset();
    if (asset && asset.id !== metadataRef.current.assetId) {
      lastRecoveryTimeRef.current = 0;
      metadataRef.current = {
        lastKnownPosition: 0,
        assetId: asset.id,
        isPlaying: player?.getJWPlayer()
          ? isPlayingState(player?.getState())
          : false,
      };
    }
  }, [player]);

  useEffect(handleNewAsset, [handleNewAsset]);

  usePlayerEvent('assetReady', handleNewAsset);
  usePlayerEvent('playlistItem', handleNewAsset);

  const handleTime = useCallback((position: number) => {
    if (position > 0) {
      metadataRef.current.lastKnownPosition = position;
    }
  }, []);
  usePlayerEvent('time', handleTime);

  const handleError = useCallback(
    ({ code }: jwplayer.ErrorParam) => {
      const now = Date.now();
      // Rate limit retries to one recovery attempt per 3s per asset
      if (lastRecoveryTimeRef.current > now - RECOVER_FROM_ERROR_THRESHOLD_MS) {
        return;
      }

      const asset = player?.getRawAsset() as SvpAssetAdditional | undefined;

      if (asset && asset.streamType !== 'live') {
        playerErrorMetadata.persist(metadataRef.current);
      }

      lastRecoveryTimeRef.current = now;

      // This is to immediately display the cast icon in the UI
      const triggerCastEvent = (newPlayer?: Player) => {
        if (!newPlayer) {
          return;
        }

        const context = cast.framework.CastContext.getInstance();
        const isAvailable =
          context.getCastState() !==
          cast.framework.CastState.NO_DEVICES_AVAILABLE;
        const session = context.getCurrentSession();

        newPlayer.getJWPlayer()?.trigger('cast', {
          available: isAvailable,
          active: Boolean(session),
          deviceName: session?.getCastDevice().friendlyName ?? '',
          type: 'cast',
        });
      };

      if (asset && asset?.id === metadataRef.current.assetId) {
        if (code === 203000) {
          // Playlist load failed
          const time = metadataRef.current.lastKnownPosition ?? 0;
          // Need to give the playlist regenerator some time
          // @see https://github.schibsted.io/vgtv/player-extras/blob/fccbfc6021f783c15ee245c5d6e569e76d1ff073/src/plugins/skin-vgtv2/hooks/ErrorRecoveryProvider.tsx#L147
          resolveIn(500)
            .then(() =>
              recreatePlayer(asset, {
                time,
                autoplay: metadataRef.current.isPlaying,
              })
            )
            .then(triggerCastEvent)
            .catch(() => {
              // noop
            });
        }
      }
    },
    [player]
  );

  usePlayerEvent('error', handleError);
};
